/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 18/02/22
 */

import React from 'react';
import { chakra, StyleProps, ThemeTypings, Tooltip } from '@chakra-ui/react';
import { SystemStyleObject } from '@chakra-ui/styled-system';
import { colourToStyle, incrementShade, toColour } from '@/wpt-lib/utils/colourUtils';
import { toTruncatedFixed } from '@/wpt-lib/utils/toTruncatedFixed';
import { toNextMultiple } from '@/wpt-lib/utils/helpers';
import { ChakraSVGGroup, ChakraSVGLine, ChakraSVGText, ChakraSVGTextSpan } from '@/components/Charts/ChakraSVG';
import { wrapToLines } from '@/utils/chart';

export interface BarChartData {
    label: string;
    amount: number;
    target: number;
    colourTheme: ThemeTypings['colorSchemes'] | string;
}

interface BarChartProps extends StyleProps {
    title: string;
    description?: string;
    axisLabel?: string;
    data: BarChartData[];
    unit?: string;
    chartAreaWidth?: number;
    nominalAxisMax?: number;
}

const BarChart = ({
    data,
    title,
    axisLabel,
    description,
    unit = '',
    chartAreaWidth = 100,
    nominalAxisMax = 100,
    ...styleProps
}: BarChartProps) => {
    const textSpace = 50;
    const height = 100;
    const width = chartAreaWidth + textSpace;
    const textSize = 4;
    const barGapSize = 8;
    const textGap = 4;
    const axisTextGap = 2;

    const top = textSize + (axisLabel ? 4 : 0);

    const nominalAxisLineCount = 6;
    const nominalAxisLineGap = nominalAxisMax / (nominalAxisLineCount - 1);

    const maxAmount = Math.max(...data.flatMap((v) => [v.amount, v.target]));
    const maxAxisLine = Math.max(nominalAxisMax, toNextMultiple(maxAmount, nominalAxisLineGap));

    const adjustedAxisGapCount = maxAxisLine / nominalAxisLineGap;
    const adjustedAxisLineCount = adjustedAxisGapCount + 1;

    const availableChartHeight = height - textSize;
    const barHeight = Math.min((availableChartHeight - barGapSize * (data.length + 1)) / data.length, 25);
    const chartAreaTop = textSize;

    return (
        <chakra.svg viewBox={`0 0 ${width} ${height}`} role="img" overflow="visible" bg="offWhite" {...styleProps}>
            <title>{title}</title>
            {description && <desc>{description}</desc>}
            {axisLabel && (
                <ChakraSVGText fontSize={textSize} textAnchor="middle" x={textSpace + (width - textSpace) / 2} y={0}>
                    {axisLabel}
                </ChakraSVGText>
            )}

            {[...Array(adjustedAxisLineCount)].map((_, i, arr) => {
                const position = i / (arr.length - 1);
                const x = textSpace + (width - textSpace) * position;
                return (
                    <ChakraSVGGroup key={i} aria-hidden={true}>
                        <ChakraSVGText x={x} y={top} fontSize={textSize} textAnchor={'middle'} bg="white">
                            {toTruncatedFixed(i * nominalAxisLineGap, 1)}
                            {unit}
                        </ChakraSVGText>
                        <ChakraSVGLine
                            x1={x}
                            y1={top + axisTextGap}
                            x2={x}
                            y2="100%"
                            stroke="gray.500"
                            strokeWidth={0.5}
                        />
                    </ChakraSVGGroup>
                );
            })}
            {data.map(({ amount, target, colourTheme, label }, i) => {
                const colour = toColour(colourTheme, 500);
                const patternLabel = label.split(' ')[0].toLowerCase();
                const barStyle: SystemStyleObject = {
                    fill: `url(#chart-pattern-${patternLabel})`,
                    stroke: 'black',
                    strokeWidth: 1,
                };
                const backgroundBarStyle: SystemStyleObject = {
                    fill: colourToStyle(colour),
                    _hover: {
                        fill: colourToStyle(incrementShade(colour)),
                    },
                    transitionDuration: '0.25s',
                };
                const x = textSpace;
                const y = chartAreaTop + barGapSize * (i + 1) + i * barHeight;

                const extraSpace = 12;
                const labelParts = wrapToLines(label, textSpace, textSize, 'NB Akademie Std');
                const labelHeight = labelParts.length * textSize + (labelParts.length - 1);
                const labelYPosition = y + barHeight / 2 - labelHeight / 2;
                const barWidth = (amount / maxAxisLine) * chartAreaWidth;
                const amountLabelXPosition = textSpace + barWidth + extraSpace;

                const patterns = () => (
                    <defs>
                        <pattern id={`chart-pattern-teaching`} patternUnits="userSpaceOnUse" width="4" height="4">
                            <rect
                                style={{
                                    fill: 'none',
                                    width: '4',
                                    height: '4',
                                }}
                            />
                            <rect
                                height="2.5"
                                width="0.5"
                                x="2.25"
                                fill="white"
                                style={{ transform: 'rotate(45deg)' }}
                            />
                            <rect
                                height="0.5"
                                width="2.5"
                                y="1"
                                x="1.25"
                                fill="white"
                                style={{ transform: 'rotate(45deg)' }}
                            />
                        </pattern>
                        <pattern id="chart-pattern-research" patternUnits="userSpaceOnUse" width="3" height="3">
                            <rect
                                style={{
                                    fill: 'none',
                                    width: '3',
                                    height: '3',
                                }}
                            />
                            <circle cx="1" cy="1" r="1" fill="white" />
                        </pattern>
                        <pattern id="chart-pattern-service" patternUnits="userSpaceOnUse" width="3" height="3">
                            <rect
                                style={{
                                    fill: 'none',
                                    width: '3',
                                    height: '3',
                                }}
                            />
                            <rect height="1.5" width="1.5" fill="white" />
                            <rect height="1.5" width="1.5" x="1.5" y="1.5" fill="white" />
                        </pattern>
                        <pattern id="chart-pattern-senior" patternUnits="userSpaceOnUse" width="4" height="4">
                            <rect width="4" height="4" fill="none" />
                            <line x1="1" y1="2" x2="3" y2="2" stroke="white" strokeWidth="0.5" />
                            <line x1="2" y1="1" x2="2" y2="3" stroke="white" strokeWidth="0.5" />
                        </pattern>
                        <pattern id="chart-pattern-professional" patternUnits="userSpaceOnUse" width="4" height="4">
                            <rect width="4" height="4" fill="none" />
                            <line x1="0" y1="4" x2="2" y2="0" stroke="white" strokeWidth="0.5" />
                            <line x1="2" y1="0" x2="4" y2="4" stroke="white" strokeWidth="0.5" />
                        </pattern>
                    </defs>
                );
                return (
                    <ChakraSVGGroup key={i} aria-hidden={true}>
                        {patterns()}
                        <ChakraSVGText
                            fontSize={textSize}
                            textAnchor={'end'}
                            dominantBaseline={'text-before-edge'}
                            y={labelYPosition}
                            bg="white"
                        >
                            {labelParts.map((labelPart, labelI) => (
                                <ChakraSVGTextSpan key={labelI} x={x - textGap} dy={labelI > 0 ? textSize + 1 : 0}>
                                    {labelPart}
                                </ChakraSVGTextSpan>
                            ))}
                        </ChakraSVGText>
                        {amount > 0 && (
                            <ChakraSVGText
                                fontSize={textSize}
                                textAnchor={'end'}
                                dominantBaseline={'text-before-edge'}
                                fontWeight="bold"
                                x={amountLabelXPosition}
                                y={labelYPosition}
                                bg="white"
                            >
                                {`${toTruncatedFixed(amount, 1)}%`}
                            </ChakraSVGText>
                        )}
                        <Tooltip label={`${labelParts.join(' ')} (${toTruncatedFixed(amount, 1)}${unit})`}>
                            <chakra.rect
                                x={x}
                                y={y}
                                height={`${barHeight}px`}
                                width={`${(target / maxAxisLine) * chartAreaWidth}px`}
                                sx={backgroundBarStyle}
                            />
                        </Tooltip>

                        <chakra.rect x={x} y={y} height={`${barHeight}px`} width={`${barWidth}px`} sx={barStyle} />
                    </ChakraSVGGroup>
                );
            })}
        </chakra.svg>
    );
};

export default BarChart;
